import type { KnownCantileverFileHosts } from 'features/knowledgeDocumentViewer/hooks/useCantileverFileHostsForDocument'
import { useCantileverFileHostsForDocument } from 'features/knowledgeDocumentViewer/hooks/useCantileverFileHostsForDocument'
import { useDocumentPageImage } from 'features/knowledgeDocumentViewer/hooks/useDocumentPageImage'

interface UseResolvedDocumentPageImageOptions {
  pageNumber?: number
  skipHiRes?: boolean
  knownHosts?: KnownCantileverFileHosts
}

interface UseResolvedDocumentPageImageResult {
  thumbnailUrl: string | undefined
  isThumbLoading: boolean
  hiResUrl: string | null
  isHiResLoading: boolean
  isHostsLoading: boolean
  refreshThumbnail: () => Promise<void>
}

/**
 * Page images via UUID5-derived (or known) cantilever hosts.
 * When bqDocumentId is known, hosts resolve immediately with no Rolodex wait.
 */
export function useResolvedDocumentPageImage(
  systemDocumentEntryId: string | undefined,
  siteId: string,
  options?: UseResolvedDocumentPageImageOptions
): UseResolvedDocumentPageImageResult {
  const pageNumber = options?.pageNumber ?? 1
  const skipHiRes = options?.skipHiRes === true
  const knownHosts = options?.knownHosts

  const {
    cantileverDocumentEntryId,
    cantileverDocumentPageId,
    filename: resolvedFilename,
    bqDocumentId,
    isLoading: isHostsLoading,
  } = useCantileverFileHostsForDocument(
    systemDocumentEntryId,
    siteId,
    pageNumber,
    knownHosts
  )

  const resolvedBqDocumentId = bqDocumentId ?? knownHosts?.bqDocumentId

  const {
    thumbnailUrl,
    isThumbLoading,
    hiResUrl,
    isHiResLoading,
    refreshThumbnail,
  } = useDocumentPageImage({
    cantileverDocumentPageId,
    cantileverDocumentEntryId,
    siteId,
    bqDocumentId: resolvedBqDocumentId,
    filename: resolvedFilename ?? knownHosts?.filename,
    skipHiRes,
    pageNumber,
  })

  // Do not block paint when UUID5 derive can proceed from a known BQ id.
  const blockOnHosts =
    isHostsLoading &&
    !resolvedBqDocumentId &&
    !knownHosts?.cantileverDocumentPageId

  return {
    thumbnailUrl,
    isThumbLoading: blockOnHosts || isThumbLoading,
    hiResUrl,
    isHiResLoading: skipHiRes ? false : blockOnHosts || isHiResLoading,
    isHostsLoading: blockOnHosts,
    refreshThumbnail,
  }
}
